import { useMemo } from 'react'
import { useOrganization } from '../contexts/OrganizationContext'
import { isDemoOrganization } from '../services/demoData'
import { getOrganizationSubscription } from '../services/featureGating'

/**
 * Hook to get subscription status and info
 */
export const useSubscriptionStatus = () => {
  const { organization, subscription } = useOrganization()

  return useMemo(() => {
    if (!organization) {
      return {
        isDemo: false,
        isTrial: false,
        isPaid: false,
        tier: null,
        daysLeft: null,
        status: null,
      }
    }

    const isDemo = isDemoOrganization(organization)
    const tier = subscription?.tier?.code || 
                 subscription?.tier || 
                 organization?.subscription_tier || 
                 'demo'
    const status = subscription?.status || 'active'
    
    // Paid tiers that should never be considered trials
    const paidTiers = ['lite', 'essential', 'professional', 'growth', 'white_label', 'enterprise']
    
    // Only consider it a trial if:
    // 1. Status is explicitly 'trial' (not 'active' - active means they've paid)
    // 2. Tier is not 'demo'
    // 3. Tier is not a paid tier (if they're on a paid tier, they're not on trial)
    // If status is 'active', they are a paid subscriber, not on trial
    const isTrial = status === 'trial' && tier !== 'demo' && !paidTiers.includes(tier)
    const isPaid = !isDemo && !isTrial && (status === 'active' || paidTiers.includes(tier))

    // Calculate days left for trial
    let daysLeft = null
    if (isTrial && subscription?.current_period_end) {
      const endDate = new Date(subscription.current_period_end)
      const now = new Date()
      const diffTime = endDate - now
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      daysLeft = diffDays > 0 ? diffDays : 0
    }

    return {
      isDemo,
      isTrial,
      isPaid,
      tier,
      daysLeft,
      status,
      subscription,
    }
  }, [organization, subscription])
}

